// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment } from "react";
import styled, { css } from "styled-components";

interface Props {
    categories: AutocompleteItemModel[] | null;
    popularCategories?: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteCategoriesStyles;
    isRetailSearchCompletionResults?: boolean;
}

export interface AutocompleteCategoriesStyles {
    headerText?: TypographyPresentationProps;
    subHeaderText?: TypographyPresentationProps;
    categoryItemText?: TypographyPresentationProps;
    divider?: InjectableCss;
    categoryItemTextInner?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    autocompleteCategoriesRowWrapper?: InjectableCss;
    autocompleteCategoriesColumnWrapper?: InjectableCss;
}

export const autocompleteCategoriesStyles: AutocompleteCategoriesStyles = {
    headerText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        color: "text.main",
        transform: "uppercase",
        css: css`
            letter-spacing: 1px;
            text-align: left;
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.light,
            lineHeight: "20px",
            color: "common.ui1",
            css: css`
                letter-spacing: 0.2px;
                display: block;
                width: 100%;
                text-align: left;
                &&& {
                    color: ${getColor("text.ui1")};
                    white-space: pre-wrap;
                }
                b {
                    color: ${getColor("text.main")};
                }
            `,
            ellipsis: true,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
        `,
    },
    leftColumn: {
        css: css`
            width: 78px;
            margin-right: 35px;
            margin-bottom: 12px;
        `,
    },
    rightColumn: {
        css: css`
            width: calc(100% - 113px);
        `,
    },
    categoryItemText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            display: block;
            width: 100%;
            text-align: left;
        `,
    },
    categoryItemTextInner: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.light,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            text-align: left;

            &:hover,
            &:focus {
                text-decoration: underline;
                cursor: pointer;
            }
        `,
    },
    divider: {
        css: css`
            min-width: 100%;
        `,
    },
    autocompleteCategoriesRowWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    autocompleteCategoriesColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = autocompleteCategoriesStyles;

const Divider = styled.hr`
    ${styles.divider?.css}
`;

class AutocompleteCategories extends React.Component<Props> {
    private readonly styles: AutocompleteCategoriesStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    renderCategories = (categories: AutocompleteItemModel[]) => {
        const styles = this.styles;
        return (
            <GridContainer gap={5}>
                {categories.map((category, index) => (
                    <GridItem key={index} width={12}>
                        {category.subtitle && (
                            <Typography {...styles.categoryItemText} className="autocomplete-categories-item-text">
                                <Link
                                    {...styles.link}
                                    className="autocomplete-categories-item-link"
                                    onClick={() => {
                                        this.props.goToUrl(category.url);
                                    }}
                                >
                                    {category.displayTitle}
                                </Link>
                                <Typography
                                    as="span"
                                    {...styles.categoryItemTextInner}
                                    onClick={() => {
                                        this.props.goToUrl(category.properties.subTitleUrl);
                                    }}
                                >{` ${translate("in")} ${category.subtitle}`}</Typography>
                            </Typography>
                        )}
                        {!category.subtitle && (
                            <Link
                                {...styles.link}
                                onClick={() => {
                                    this.props.goToUrl(category.url);
                                }}
                                className="autocomplete-categories-link"
                            >
                                {category.displayTitle}
                            </Link>
                        )}
                    </GridItem>
                ))}
            </GridContainer>
        );
    };

    render() {
        const { categories, popularCategories, isRetailSearchCompletionResults } = this.props;
        if ((!categories || categories.length === 0) && (!popularCategories || popularCategories.length === 0)) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper
                    {...styles.leftColumn}
                    data-test-selector="autocompleteCategories_Header"
                    className="autocomplete-categories-header"
                >
                    <Typography {...styles.headerText}>{translate("Categories")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    {categories &&
                        categories.length > 0 &&
                        !isRetailSearchCompletionResults &&
                        this.renderCategories(categories)}

                    {isRetailSearchCompletionResults && (
                        <>
                            <StyledWrapper
                                {...this.styles.autocompleteCategoriesRowWrapper}
                                className="autocomplete-categories-row-wrapper"
                            >
                                {popularCategories && popularCategories.length > 0 && (
                                    <StyledWrapper
                                        {...this.styles.autocompleteCategoriesColumnWrapper}
                                        className="autocomplete-categories-column-wrapper"
                                    >
                                        <Typography
                                            {...styles.subHeaderText}
                                            data-test-selector="autocompleteCategories_subHeader"
                                            className="autocomplete-categories-subheader"
                                        >
                                            {translate("popular")}
                                        </Typography>
                                        {this.renderCategories(popularCategories)}
                                    </StyledWrapper>
                                )}
                                {categories && categories.length > 0 && (
                                    <StyledWrapper
                                        {...this.styles.autocompleteCategoriesColumnWrapper}
                                        className="autocomplete-categories-column-wrapper"
                                    >
                                        <Typography
                                            {...styles.subHeaderText}
                                            data-test-selector="autocompleteCategories_subHeader"
                                            className="autocomplete-categories-subheader"
                                        >
                                            {translate("suggested")}
                                        </Typography>
                                        {this.renderCategories(categories)}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteCategories;
